import type { Character, Side } from '../data/types';
import { GameEngine } from '../game/engine';
import { spriteUrl, saveBattle } from '../data/api';
import { SFX, startMusic, stopMusic, unlockAudio } from '../sound';
import {
  checkAchievements, recordCharResult, recordWinStreak,
  checkFlawless, checkComeback, showToast,
} from '../achievements';

interface SpellDef { id: string; emoji: string; label: string; cost: number; cooldown: number; }
const SPELLS: SpellDef[] = [
  { id: 'fireball',  emoji: '🔥', label: 'Fireball',  cost: 25, cooldown: 4 },
  { id: 'freeze',    emoji: '❄️', label: 'Freeze',    cost: 20, cooldown: 5 },
  { id: 'lightning', emoji: '⚡', label: 'Lightning', cost: 35, cooldown: 6 },
  { id: 'heal',      emoji: '💚', label: 'Heal',      cost: 30, cooldown: 5 },
  { id: 'meteor',    emoji: '☄️', label: 'Meteor',    cost: 50, cooldown: 10 },
  { id: 'shield',    emoji: '🛡️', label: 'Shield',    cost: 25, cooldown: 6 },
  { id: 'ultimate',  emoji: '⭐', label: 'ULTIMATE',  cost: 0,  cooldown: 0 },
];

function announce(text: string) {
  try {
    const synth = window.speechSynthesis;
    if (!synth) return;
    const u = new SpeechSynthesisUtterance(text);
    u.rate = 1.05; u.pitch = 1.0; u.volume = 1;
    const voices = synth.getVoices();
    const en = voices.find(v => /en[-_]/i.test(v.lang)) || voices[0];
    if (en) u.voice = en;
    synth.speak(u);
  } catch {}
}

const STREAK_LABELS: Record<number, string> = {
  2: 'DOUBLE KILL!',
  3: 'TRIPLE KILL!',
  4: 'QUAD KILL!',
  5: 'RAMPAGE!',
  6: 'UNSTOPPABLE!',
  7: 'GODLIKE!',
};

function showBanner(root: HTMLElement, text: string, color: string) {
  const b = document.createElement('div');
  b.className = 'kill-banner';
  b.textContent = text;
  b.style.color = color;
  root.querySelector('.battle-screen')?.appendChild(b);
  setTimeout(() => b.remove(), 1800);
}

let _flashEl: HTMLElement | null = null;
function flashScreen(color: string) {
  if (!_flashEl) {
    _flashEl = document.createElement('div');
    _flashEl.className = 'screen-flash';
    document.body.appendChild(_flashEl);
  }
  _flashEl.style.background = color;
  _flashEl.style.opacity = '0.45';
  setTimeout(() => { if (_flashEl) _flashEl.style.opacity = '0'; }, 50);
}

export function renderBattle(
  root: HTMLElement,
  left: Character[],
  right: Character[],
  onDone: () => void
) {
  root.innerHTML = `
    <div class="battle-screen">
      <canvas id="game-canvas"></canvas>
      <div class="battle-hud">
        <div class="team-roster left" id="left-roster"></div>
        <div class="team-roster right" id="right-roster"></div>
      </div>
      <div class="battle-top-bar">
        <button class="btn-secondary" id="back-btn">← Menu</button>
      </div>
      <div class="mana-bar"><div class="mana-fill" id="mana-fill" style="width:50%"></div></div>
      <div class="ult-bar"><div class="ult-fill" id="ult-fill" style="width:0%"></div><span class="ult-label">⭐ ULT</span></div>
      <div class="weather-tag" id="weather-tag"></div>
      <div class="weather-tag" id="modifier-tag" style="top:96px"></div>
      <div class="spell-bar" id="spell-bar"></div>
    </div>
  `;

  const canvas = root.querySelector('#game-canvas') as HTMLCanvasElement;
  void canvas.offsetHeight;
  unlockAudio();
  startMusic();
  const engine = new GameEngine(canvas);
  // After the next frame, re-fit in case CSS layout shifted dimensions.
  requestAnimationFrame(() => engine.onResize());
  let mana = 50;
  const maxMana = 100;
  const spellCds: Record<string, number> = {};

  let ultReady = false;

  // build spell bar
  const spellBar = root.querySelector('#spell-bar') as HTMLElement;
  SPELLS.forEach(s => {
    const btn = document.createElement('button');
    btn.className = 'spell-btn' + (s.id === 'ultimate' ? ' ult-btn' : '');
    btn.dataset.id = s.id;
    btn.innerHTML = `${s.emoji}<div class="label">${s.label}</div><div class="cooldown" style="display:none"></div>`;
    btn.addEventListener('click', () => {
      if (s.id === 'ultimate') {
        if (!ultReady) return;
      } else {
        const cd = spellCds[s.id] ?? 0;
        if (cd > 0 || mana < s.cost) return;
      }
      document.querySelectorAll('.spell-btn').forEach(b => b.classList.remove('selected'));
      btn.classList.add('selected');
      engine.pendingSpell = s.id as any;
      SFX.whoosh();
    });
    spellBar.appendChild(btn);
  });

  // ----- mana bonus from kills -----
  const manaBonusHandler = (e: Event) => {
    const d = (e as CustomEvent).detail;
    mana = Math.min(maxMana, mana + (d?.amount ?? 0));
  };
  window.addEventListener('mana-bonus', manaBonusHandler);

  // ----- ultimate meter (track player's "left" side) -----
  const ultFill = root.querySelector('#ult-fill') as HTMLElement;
  const ultBtn = () => root.querySelector('.spell-btn.ult-btn') as HTMLButtonElement | null;
  const ultHandler = (e: Event) => {
    const d = (e as CustomEvent).detail;
    if (d.side !== 'left') return; // player is left
    const pct = Math.min(1, d.value / d.max);
    ultFill.style.width = (pct * 100) + '%';
    ultReady = pct >= 1;
    const b = ultBtn();
    if (b) {
      b.disabled = !ultReady;
      b.classList.toggle('ready', ultReady);
    }
  };
  window.addEventListener('ult-update', ultHandler);
  // initialize button as locked
  setTimeout(() => { const b = ultBtn(); if (b) b.disabled = true; }, 0);

  // ----- battle events: fight start, kills, weather, reinforcements -----
  const battleEventHandler = (e: Event) => {
    const d = (e as CustomEvent).detail;
    if (d.kind === 'fight') {
      announce('Fight!');
      SFX.fight();
      showBanner(root, 'FIGHT!', '#ffd84d');
      flashScreen('#ffd84d');
    } else if (d.kind === 'kill') {
      if (d.firstBlood) {
        announce('First Blood!');
        showBanner(root, 'FIRST BLOOD!', '#ff5277');
      }
      const label = STREAK_LABELS[d.streak];
      if (label) {
        announce(label.replace('!', ''));
        showBanner(root, label, d.side === 'left' ? '#4dd0ff' : '#ff5277');
        SFX.killStreak();
      }
    } else if (d.kind === 'weather') {
      const tag = root.querySelector('#weather-tag') as HTMLElement;
      const map: Record<string, string> = {
        clear: '', rain: '🌧️ Rain', thunderstorm: '⛈️ Thunderstorm', sandstorm: '🌪️ Sandstorm',
      };
      tag.textContent = map[d.weather] || '';
    } else if (d.kind === 'reinforcements') {
      announce('Reinforcements!');
      showBanner(root, 'REINFORCEMENTS!', '#ffd84d');
    } else if (d.kind === 'modifier') {
      const tagEl = root.querySelector('#modifier-tag') as HTMLElement;
      const map: Record<string, string> = {
        meteor_storm: '☄️ Meteor Storm',
        double_mana:  '💎 Double Mana',
        half_hp:      '💔 Half HP',
        lava_floor:   '🌋 Lava Floor',
        sudden_death: '⚰️ Sudden Death',
      };
      if (tagEl) tagEl.textContent = map[d.modifier] || '';
      announce((map[d.modifier] || '').replace(/^\W+\s/, ''));
      showBanner(root, (map[d.modifier] || '').toUpperCase(), '#ffaa44');
    } else if (d.kind === 'boss') {
      announce(d.name + ' approaches!');
      showBanner(root, 'BOSS: ' + d.name.toUpperCase(), '#ff3366');
      flashScreen('#ff3366');
    } else if (d.kind === 'showdown') {
      announce('Final showdown!');
      showBanner(root, 'FINAL SHOWDOWN!', '#ffd84d');
      flashScreen('#ffd84d');
    }
  };
  window.addEventListener('battle-event', battleEventHandler);

  const spellCastHandler = () => {
    const sel = document.querySelector('.spell-btn.selected') as HTMLElement | null;
    if (!sel) return;
    const id = sel.dataset.id!;
    const def = SPELLS.find(s => s.id === id)!;
    if (id !== 'ultimate') {
      mana -= def.cost;
      spellCds[id] = def.cooldown;
    } else {
      ultReady = false;
      const b = ultBtn(); if (b) { b.disabled = true; b.classList.remove('ready'); }
    }
    sel.classList.remove('selected');
  };
  window.addEventListener('spell-cast', spellCastHandler);

  // mana regen + cooldown ticker
  const tick = setInterval(() => {
    const regenAmt = (engine.modifier === 'double_mana') ? 4 : 2;
    mana = Math.min(maxMana, mana + regenAmt);
    const fill = root.querySelector('#mana-fill') as HTMLElement;
    if (fill) fill.style.width = `${(mana / maxMana) * 100}%`;
    SPELLS.forEach(s => {
      if (s.id === 'ultimate') return;
      if ((spellCds[s.id] ?? 0) > 0) spellCds[s.id]! -= 0.1;
      const btn = root.querySelector(`.spell-btn[data-id="${s.id}"]`) as HTMLButtonElement;
      if (!btn) return;
      const cdEl = btn.querySelector('.cooldown') as HTMLElement;
      const cd = spellCds[s.id] ?? 0;
      const wasDisabled = btn.disabled;
      if (cd > 0) {
        cdEl.style.display = 'flex';
        cdEl.textContent = cd.toFixed(1);
        btn.disabled = true;
      } else if (mana < s.cost) {
        cdEl.style.display = 'none';
        btn.disabled = true;
      } else {
        cdEl.style.display = 'none';
        btn.disabled = false;
        if (wasDisabled) SFX.spellReady();
      }
    });
  }, 100);

  // build rosters
  function buildRoster(arr: Character[], side: Side) {
    const el = root.querySelector(`#${side}-roster`) as HTMLElement;
    arr.forEach((c, i) => {
      const row = document.createElement('div');
      row.className = 'roster-row';
      row.dataset.idx = String(i);
      row.dataset.side = side;
      row.innerHTML = `
        <img src="${spriteUrl(c)}">
        <div style="flex:1;font-size:11px;">${c.name}</div>
        <div class="hp-mini"><div class="hp-mini-fill" style="width:100%"></div></div>
      `;
      el.appendChild(row);
    });
  }
  buildRoster(left, 'left');
  buildRoster(right, 'right');

  // engine setup
  (async () => {
    await engine.loadTeams(left, right);
    // wire roster updates
    const rosterTick = setInterval(() => {
      engine.units.forEach((u, i) => {
        const arr = u.side === 'left' ? left : right;
        const idx = arr.indexOf(u.character);
        const row = root.querySelector(`#${u.side}-roster .roster-row[data-idx="${idx}"]`) as HTMLElement;
        if (row) {
          const fill = row.querySelector('.hp-mini-fill') as HTMLElement;
          fill.style.width = `${(u.hp / u.maxHp) * 100}%`;
          if (u.dead) row.classList.add('dead');
        }
      });
    }, 100);
    engine.onWin = (winner, duration) => {
      clearInterval(tick);
      clearInterval(rosterTick);
      window.removeEventListener('mana-bonus', manaBonusHandler);
      window.removeEventListener('ult-update', ultHandler);
      window.removeEventListener('battle-event', battleEventHandler);
      window.removeEventListener('spell-cast', spellCastHandler);
      stopMusic();
      announce(winner === 'left' ? 'Team Left wins!' : 'Team Right wins!');
      saveBattle(left.map(c=>c.id), right.map(c=>c.id), winner, duration);

      // ----- MVP + per-character lifetime stats -----
      const winningTeam = winner === 'left' ? left : right;
      const winningUnits = engine.units.filter(u => u.side === winner);
      let mvp: any = null;
      let mvpScore = -1;
      const teamFactions = new Set<string>();
      const playerWon = winner === 'left'; // player is always left
      for (const u of engine.units) {
        const kills = (u as any)._kills ?? 0;
        const dmg = (u as any)._dmg ?? 0;
        const won = u.side === winner;
        recordCharResult(u.character.id, won, kills, dmg);
        if (won) {
          teamFactions.add(u.character.faction);
          const score = kills * 100 + dmg;
          if (score > mvpScore) { mvpScore = score; mvp = u; }
        }
      }
      const survivors = winningUnits.filter(u => !u.dead).length;

      // ----- achievements -----
      const newAch: any[] = [];
      const hadLegendary = winningTeam.some(c =>
        ['poke-mewtwo','poke-arceus','poke-rayquaza','poke-dialga','poke-palkia'].includes(c.id));
      newAch.push(...checkAchievements({
        won: playerWon,
        survivors,
        factions: Array.from(teamFactions),
        hadLegendary,
        critsLanded: engine.critsLanded,
        ultsCast: engine.ultsCast,
      }));
      if (playerWon) {
        const fl = checkFlawless(survivors, winningTeam.length);
        if (fl) newAch.push(fl);
        const cb = checkComeback(survivors, true);
        if (cb) newAch.push(cb);
      }
      const streak = recordWinStreak(playerWon);

      showVictoryEnhanced(root, winner, mvp, newAch, streak, onDone);
    };
    engine.start();
  })();

  root.querySelector('#back-btn')!.addEventListener('click', () => {
    clearInterval(tick);
    stopMusic();
    window.removeEventListener('mana-bonus', manaBonusHandler);
    window.removeEventListener('ult-update', ultHandler);
    window.removeEventListener('battle-event', battleEventHandler);
    window.removeEventListener('spell-cast', spellCastHandler);
    engine.stop();
    onDone();
  });
}

function showVictoryEnhanced(
  root: HTMLElement,
  winner: Side,
  mvp: any,
  newAch: any[],
  streak: number,
  onDone: () => void,
) {
  const overlay = document.createElement('div');
  overlay.className = 'victory-overlay';
  const mvpHtml = mvp ? `
    <div class="mvp-card">
      <div class="mvp-label">⭐ MATCH MVP ⭐</div>
      <img class="mvp-img" src="${spriteUrl(mvp.character)}">
      <div class="mvp-name">${mvp.character.name}</div>
      <div class="mvp-stats">
        <span>⚔️ ${(mvp as any)._kills ?? 0} KO</span>
        <span>💥 ${Math.round((mvp as any)._dmg ?? 0)} DMG</span>
      </div>
    </div>
  ` : '';
  overlay.innerHTML = `
    <div class="victory-title">${winner === 'left' ? 'TEAM LEFT' : 'TEAM RIGHT'} WINS!</div>
    <div class="victory-sub">${streak > 1 ? `🔥 ${streak} Win Streak!` : 'Glorious victory!'}</div>
    ${mvpHtml}
    <div class="victory-buttons">
      <button class="btn-primary" id="rematch-btn">⚔️ New Battle</button>
    </div>
  `;
  root.querySelector('.battle-screen')!.appendChild(overlay);
  overlay.querySelector('#rematch-btn')!.addEventListener('click', onDone);

  // Stagger achievement toasts
  newAch.forEach((a, i) => setTimeout(() => showToast(a, document.body), 600 + i * 800));
}
